import { Effect, pipe } from "effect";
import { MerklCampaignMeta } from "../merkl-client/types";
import { KvClientError, KvService } from "./types";
import { getMerklCampaignMetaKey } from "./keys";
import { Schema } from "@effect/schema";
import { TTL } from "./constants";

export type SetMerklCampaignMeta = ReturnType<typeof setMerklCampaignMeta>;
export type SetMerklCampaignMetaSuccess =
  Effect.Effect.Success<SetMerklCampaignMeta>;
export type SetMerklCampaignMetaError =
  Effect.Effect.Error<SetMerklCampaignMeta>;
export type SetMerklCampaignMetaContext =
  Effect.Effect.Context<SetMerklCampaignMeta>;

export function setMerklCampaignMeta(
  merklCampaignMetaArray: MerklCampaignMeta[]
) {
  return Effect.gen(function* () {
    const kv = yield* KvService;
    const key = getMerklCampaignMetaKey();

    const encoded = yield* pipe(
      merklCampaignMetaArray,
      Schema.encode(Schema.Array(MerklCampaignMeta))
    );

    yield* Effect.tryPromise({
      try: async () => {
        return kv.set(key, encoded, { ex: TTL.TEN_MINUTES });
      },
      catch: (cause) => new KvClientError({ cause }),
    });
  });
}

export type GetMerklCampaignMeta = ReturnType<typeof getMerklCampaignMeta>;
export type GetMerklCampaignMetaSuccess =
  Effect.Effect.Success<GetMerklCampaignMeta>;
export type GetMerklCampaignMetaError =
  Effect.Effect.Error<GetMerklCampaignMeta>;
export type GetMerklCampaignMetaContext =
  Effect.Effect.Context<GetMerklCampaignMeta>;

export function getMerklCampaignMeta({ activeOnly }: { activeOnly: boolean }) {
  return Effect.gen(function* () {
    const kv = yield* KvService;
    const key = getMerklCampaignMetaKey();

    const result = yield* Effect.tryPromise({
      try: async () => {
        return kv.get(key);
      },
      catch: (cause) => new KvClientError({ cause }),
    });

    const decoded = yield* pipe(
      result,
      Schema.decodeUnknown(Schema.Array(MerklCampaignMeta))
    );

    const filteredCampaigns = decoded.filter((campaign) => {
      if (!activeOnly) return true;

      return campaign.isActive;
    });

    return filteredCampaigns;
  });
}
